import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { Subject, firstValueFrom } from 'rxjs';

import { AUTHENTICATION_GUARDS } from '../master-authetication/guard-collection';
import { CanActivateIsLoggedInOneIdGuard } from '../authentication/one-id/is-logged-in-one-id-guard';
import { CanActivateIsInitializedOneIdGuard } from '../one-id-initialization/one-id-initialization.guard';

/**
 * MasterAuthenticationGuard must be used to execute guards sequentially on the same route,
 * for this the guards must be indicated in the routing module of the component:
 *
 *  canActivate: [
 *        MasterAuthenticationGuard
 *   ],
 *    data: {
 *        guards: [
 *            AUTHENTICATION_GUARDS.ONE_ID_GUARD,
 *            AUTHENTICATION_GUARDS.AUTHENTICATION_ONE_ID_GUARD
 *        ]
 *
 *  then CanActive of this class is responsible for executing the passed guards sequentially using a recursive function
 *  and resolves with true until every child guard is resolved successfully.
 */
@Injectable()
export class MasterAuthenticationGuard implements CanActivate {
    constructor(
        private canActivateOneIdInGuard: CanActivateIsInitializedOneIdGuard,
        private canActivateIsLoggedInOneIdGuard: CanActivateIsLoggedInOneIdGuard,
    ) {}

    private route: ActivatedRouteSnapshot;
    private state: RouterStateSnapshot;
    public loader$ = new Subject<boolean>();
    public hasGuards = true;

    //This method gets triggered when the route is hit
    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        this.loader$.next(true);
        this.route = route;
        this.state = state;

        if (!route.data) {
            Promise.resolve(true);

            return;
        }

        // Validates if master guard contains othe guards
        this.hasGuards = Boolean(this.route.data.guards && this.route.data.guards.length);
        //this.route.data.guards is an array of strings set in routing configuration
        if (!this.route.data.guards || !this.route.data.guards.length) {
            Promise.resolve(true);

            return;
        }

        return this.executeGuards();
    }

    //Execute the guards sent in the route data
    private async executeGuards(guardIndex: number = 0): Promise<boolean> {
        return this.activateGuard(this.route.data.guards[guardIndex])
            .then(() => {
                if (guardIndex < this.route.data.guards.length - 1) {
                    return this.executeGuards(guardIndex + 1);
                } else {
                    // finishes all guards
                    this.loader$.next(false);

                    return Promise.resolve(true);
                }
            })
            .catch(() => {
                return Promise.reject(false);
            });
    }

    //Create an instance of the guard and fire canActivate method returning a promise
    private activateGuard(guardKey: string): Promise<boolean> {
        switch (guardKey) {
            case AUTHENTICATION_GUARDS.ONE_ID_INITIALIZATION_GUARD:
                return this.canActivateOneIdInGuard.canActivate(this.route);
            case AUTHENTICATION_GUARDS.AUTHENTICATION_ONE_ID_GUARD:
                return firstValueFrom(this.canActivateIsLoggedInOneIdGuard.canActivate(this.route, this.state));
            default:
                break;
        }
    }
}
